import { useState } from 'react';
import { Navigate, useParams } from 'react-router';
import { mutateConfig } from '../api/mutationConfig';
import { THEMES_KEY, themeUrl, useGetProgramTheme, useRefreshThemes, type ProgramThemePayload } from '../api/useProgramThemes';
import { DetailHeader } from '../components/DetailSection';
import { Notice } from '../components/Notice';
import { ThemeActions } from '../components/ThemeActions';
import { ThemeForm } from '../components/ThemeForm';
import { ThemeStatusBadge } from '../components/ThemeStatusBadge';
import { ThemeSummary } from '../components/ThemeSummary';
import { useFlashNotice } from '../hooks/useFlashNotice';
import { getErrorMessage } from '../lib/apiError';

export const ThemeDetail = () => {
  const { uid = '' } = useParams();
  const [notice, setNotice] = useFlashNotice();
  const [saved, setSaved] = useState('');
  const refreshThemes = useRefreshThemes();
  const { data, isLoading, isError } = useGetProgramTheme(uid);
  const update = mutateConfig({ method: 'patch' })<unknown>({ url: themeUrl(uid), cacheKeys: [THEMES_KEY] });

  if (!uid) return <Navigate to="/themes" replace />;
  if (isLoading) return <div className="text-sm text-gray-500">Loading...</div>;

  const theme = data?.payload;
  if (isError || !theme) return <div className="text-sm text-red-500">Theme not found.</div>;

  const handleSave = (changes: Partial<ProgramThemePayload>) => {
    setSaved('');
    update.mutate(changes, {
      onSuccess: () => {
        setSaved('Changes saved.');
        refreshThemes();
      },
    });
  };

  return (
    <div className="flex flex-col gap-6">
      <DetailHeader backTo="/themes" backLabel="Program themes" title={theme.name} badges={<ThemeStatusBadge theme={theme} />} />
      {notice && <Notice tone="success">{notice}</Notice>}
      {theme.archived ? (
        <>
          <Notice>
            This theme is archived. Programs already on it keep it, but it can't be edited, made the default, or given to
            new programs.
          </Notice>
          <ThemeSummary theme={theme} />
        </>
      ) : (
        <>
          <ThemeActions
            theme={theme}
            onNotice={(n) => {
              setSaved('');
              setNotice(n);
            }}
          />
          <Notice tone="warning">
            Edits show on every program using this theme, except where an organizer has replaced that image or colour
            with their own.
          </Notice>
          <ThemeForm
            key={theme.updated_at}
            theme={theme}
            submitLabel="Save changes"
            isSaving={update.isPending}
            error={update.error ? getErrorMessage(update.error, "Couldn't save the theme. Try again.") : undefined}
            success={saved}
            onSubmit={handleSave}
          />
        </>
      )}
    </div>
  );
};
